import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { PLATFORM_COLORS } from "../lib/constants";
import { Users, Search, X, Plus, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

// Mod-only: crew a case up with existing cases spotted after the original upload.
export const LinkTeammate = ({ reportId, currentGamertag, onLinked }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (timer.current) clearTimeout(timer.current);
    const term = q.trim();
    if (term.length < 2) { setResults([]); return; }
    setSearching(true);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/reports/search-cases`, { params: { q: term } });
        const chosen = new Set(selected.map((s) => s.id));
        setResults((data || []).filter((r) => r.id !== reportId && !chosen.has(r.id)));
      } catch { /* ignore */ } finally { setSearching(false); }
    }, 300);
    return () => timer.current && clearTimeout(timer.current);
  }, [q, open, reportId, selected]);

  const add = (r) => { setSelected((s) => [...s, r]); setResults((rs) => rs.filter((x) => x.id !== r.id)); setQ(""); };
  const remove = (id) => setSelected((s) => s.filter((x) => x.id !== id));

  const save = async () => {
    if (!selected.length) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/reports/${reportId}/link-teammate`, {
        teammate_report_ids: selected.map((s) => s.id),
      });
      toast.success(`Linked ${selected.length} teammate${selected.length === 1 ? "" : "s"} to ${currentGamertag}.`);
      setSelected([]); setQ(""); setResults([]); setOpen(false);
      onLinked && onLinked(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not link teammates.");
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-4 rounded-lg border border-[#7C3AED]/25 bg-[#7C3AED]/[0.06] p-3" data-testid="link-teammate-box">
      <div className="flex flex-wrap items-center gap-2">
        <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#C084FC]">
          <Users size={13} /> Crew links <span className="font-normal normal-case text-[#94A3B8]">(mod only · tag players who run together)</span>
        </span>
        {!open && (
          <button data-testid="link-teammate-toggle" onClick={() => setOpen(true)}
            className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-[#7C3AED]/40 px-2.5 py-1 text-xs font-semibold text-[#C084FC] transition-colors hover:bg-[#7C3AED]/15">
            <Plus size={13} /> Link teammate
          </button>
        )}
      </div>

      {open && (
        <div className="mt-3 space-y-2" data-testid="link-teammate-panel">
          {selected.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="link-teammate-selected">
              {selected.map((s) => (
                <span key={s.id} className="inline-flex items-center gap-1 rounded-full border border-[#7C3AED]/40 bg-[#7C3AED]/15 px-2 py-0.5 text-xs font-semibold text-[#C084FC]">
                  {s.gamertag}
                  <button onClick={() => remove(s.id)} data-testid={`link-teammate-remove-${s.id}`} className="text-[#94A3B8] hover:text-white"><X size={12} /></button>
                </span>
              ))}
            </div>
          )}
          <div className="relative">
            <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[#64748B]" />
            <input
              data-testid="link-teammate-search"
              autoFocus value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search a cheater's gamertag…"
              className="w-full rounded-lg border border-[#1E293F] bg-[#141B2D] py-1.5 pl-8 pr-3 text-sm text-white placeholder:text-[#64748B] focus:border-[#7C3AED]/50 focus:outline-none"
            />
            {searching && <Loader2 size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 animate-spin text-[#7C3AED]" />}
          </div>
          {results.length > 0 && (
            <div className="max-h-44 overflow-y-auto rounded-lg border border-[#1E293F] bg-[#0B0F1C]" data-testid="link-teammate-results">
              {results.map((r) => {
                const c = PLATFORM_COLORS[r.platform] || "#94A3B8";
                return (
                  <button key={r.id} data-testid={`link-teammate-result-${r.id}`} onClick={() => add(r)}
                    className="flex w-full items-center gap-2 border-b border-[#1E293F] px-3 py-2 text-left text-sm transition-colors last:border-0 hover:bg-[#7C3AED]/10">
                    <span className="font-semibold text-white">{r.gamertag}</span>
                    <span className="rounded px-1.5 py-0.5 text-[11px] font-mono" style={{ color: c, backgroundColor: `${c}18` }}>{r.platform}</span>
                    <span className="truncate text-xs text-[#64748B]">{r.cheat_type}</span>
                    <Plus size={13} className="ml-auto shrink-0 text-[#C084FC]" />
                  </button>
                );
              })}
            </div>
          )}
          {q.trim().length >= 2 && !searching && results.length === 0 && (
            <p className="text-xs text-[#64748B]" data-testid="link-teammate-empty">No matching cases found.</p>
          )}
          <div className="flex items-center gap-2 pt-1">
            <button data-testid="link-teammate-save" onClick={save} disabled={!selected.length || saving}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#7C3AED] px-3 py-1.5 text-sm font-bold text-white transition-colors hover:bg-[#6D28D9] disabled:opacity-40">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              Link {selected.length || ""} teammate{selected.length === 1 ? "" : "s"}
            </button>
            <button data-testid="link-teammate-cancel" onClick={() => { setOpen(false); setSelected([]); setQ(""); setResults([]); }}
              className="rounded-lg border border-[#1E293F] bg-[#141B2D] px-3 py-1.5 text-sm text-[#94A3B8] hover:text-white">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
